import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import ContactForm from "../components/ContactForm";
import { breadcrumbSchema, serviceSchema } from "../lib/schemas";

const REFERENCE_CASES = [
  {
    to: "/works/ac-milan-connect",
    chip: "Business Impact · Platform sponsorship",
    title: "AC Milan Connect",
    kpi: "Da sponsorship a prodotto: 1° club al mondo con SIM dedicata",
    image:
      "/images/assets/gsvgr6p9_1698141384711.jpg",
  },
  {
    to: "/works/noleggiare-hellas",
    chip: "Cultural Impact · Brand Experience",
    title: "Noleggiare × Hellas × Infront",
    kpi: "Da logo a esperienza: il format The Dugout",
    image:
      "/images/assets/e7iivete_20240308_12-58-02_z81_8211-980x1470.jpg",
  },
  {
    to: "/works/reale-mutua-essere-libero",
    chip: "Cultural Impact · Format proprietario",
    title: "Reale Mutua × Essere Libero",
    kpi: "Una sponsorship dentro un format proprietario",
    image:
      "/images/assets/e4nbqsyw_01.jpg",
  },
];

const METHOD_STEPS = [
  {
    num: "01",
    title: "Audit asset-per-asset",
    body:
      "Mappa di tutti gli asset contrattuali della sponsorship (esistente o in trattativa). Per ognuno: che valore può generare, come si attiva, su che KPI si misura.",
  },
  {
    num: "02",
    title: "Progettazione platform",
    body:
      "Trasformazione della sponsorship da diritti a piattaforma. Definizione di programmi tematici annuali, attivazioni stagionali, contenuti coordinati, partnership di amplificazione.",
  },
  {
    num: "03",
    title: "Esecuzione stagionale",
    body:
      "Attivazione operativa dei programmi: matchday, eventi, produzione contenuti, gestione testimonial, integrazione con i canali del cliente. Coordinamento continuativo con la property.",
  },
  {
    num: "04",
    title: "Reporting & rinnovo",
    body:
      "Reporting trimestrale e annuale sui KPI condivisi. Strategia di rinnovo (o uscita) basata sui dati di performance. Negoziazione delle condizioni del ciclo successivo.",
  },
];

const DELIVERABLES = [
  { output: "Audit del portafoglio sponsorship", when: "Settimana 1-3", to: "C-level + Marketing" },
  { output: "Scorecard portafoglio (Sponsorship Impact Optimizer)", when: "Settimana 4", to: "C-level + CFO" },
  { output: "Piano di attivazione platform", when: "Settimana 5-8", to: "Marketing operations" },
  { output: "Calendario operativo stagionale", when: "Ricorrente", to: "Team interno + property" },
  { output: "Produzione contenuti & attivazioni", when: "Stagionale", to: "Tutti i canali" },
  { output: "Reporting trimestrale 3D", when: "Trimestrale", to: "C-level + Marketing" },
  { output: "Strategia di rinnovo/uscita", when: "Fine ciclo", to: "C-level" },
];

const PROFILES = [
  {
    title: "Brand con portafoglio sponsorship",
    body:
      "Aziende corporate, consumer o B2B che gestiscono 3+ sponsorship attive e vogliono ottimizzarne il ROI complessivo.",
  },
  {
    title: "Brand al primo investimento sportivo",
    body:
      "Aziende che firmano la prima sponsorship o partnership e vogliono costruirla bene fin dall'inizio.",
  },
  {
    title: "Club, leghe, federazioni",
    body:
      "Property sportive che vendono sponsorship e vogliono trasformare la propria proposta commerciale.",
  },
  {
    title: "Agenzie commerciali",
    body:
      "Agenzie che gestiscono i diritti commerciali di property sportive e cercano un partner strategico per attivare le partnership che vendono.",
  },
];

const KPI_TYPES = [
  {
    type: "Business",
    examples: "Lead generati, CAC, conversione, AOV, revenue attribuibile, ROAS",
  },
  {
    type: "Cultural",
    examples: "Brand salience qualificata, share of voice tematico, sentiment qualitativo",
  },
  {
    type: "Social",
    examples: "Beneficiari diretti, impatto territoriale, ESG reporting",
  },
  {
    type: "Asset",
    examples: "% asset contrattuali effettivamente attivati, % valore realizzato vs nominale",
  },
];

const FAQS = [
  {
    q: "Lavorate solo su sponsorship già esistenti?",
    a:
      "No. Lavoriamo sia su sponsorship già attive (ottimizzazione) sia su nuove partnership in fase di negoziazione (progettazione iniziale). Il framework è lo stesso.",
  },
  {
    q: "Cosa fate diversamente da un'agenzia di sponsorship tradizionale?",
    a:
      "Le agenzie tradizionali vendono attivazioni a progetto (un evento, una campagna). Noi progettiamo piattaforme pluriennali con framework di misurazione condivisi. Lavoriamo sul programma, non sul singolo deliverable.",
  },
  {
    q: "Lavorate solo con il calcio?",
    a:
      "No. Lavoriamo con calcio, volley, basket, motorsport, multi-sport. La metodologia è agnostica rispetto alla disciplina — cambiano i KPI specifici e i partner commerciali, non l'approccio.",
  },
  {
    q: "Quanto dura un programma di Sponsorship Activation?",
    a:
      "Dipende dalla scala della partnership e dal calendario sportivo della property coinvolta. Un programma di attivazione standard è progettato come ciclo annuale, con possibilità di estensione pluriennale. La configurazione esatta del progetto la definiamo insieme al cliente dopo l'audit iniziale.",
  },
  {
    q: "Cosa è lo Sponsorship Impact Optimizer?",
    a:
      "È il nostro framework proprietario per ottimizzare un portafoglio di sponsorship esistente. 7 passi, durata 6-8 settimane, output: scorecard del portafoglio + piano di re-attivazione + framework di misurazione condiviso.",
  },
];

const ServizioSponsorship = () => {
  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Servizi", url: "/servizi" },
    { name: "Sponsorship & Impact Programs", url: "/servizi/sponsorship-impact-programs" },
  ]);
  const serviceJsonLd = serviceSchema({
    name: "Sponsorship & Impact Programs",
    description: "Programmi di sponsorship sportiva integrati: strategia, attivazione, content, misurazione.",
    slug: "sponsorship-impact-programs",
  });

  return (
    <>
      <Seo
        title="Sponsorship Programs: trasforma sponsorship in piattaforma | OnSports"
        description="Costruiamo programmi di sponsorship sportiva integrati: strategia, attivazione, content, misurazione. Per brand title sponsor e proprietà sportive italiane."
        path="/servizi/sponsorship-impact-programs"
        jsonLd={[breadcrumbJsonLd, serviceJsonLd]}
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-24 md:pt-52 md:pb-28"
        data-testid="hero-sponsorship"
      >
        <div className="container-os">
          <RevealOnScroll>
            <Link
              to="/servizi"
              className="inline-flex items-center gap-2 text-galactico/70 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-servizi"
            >
              <ArrowLeft size={16} /> Tutti i servizi
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <SectionEyebrow num="02" className="mt-8">
              Sponsorship &amp; Impact Programs
            </SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1}>
            <span className="inline-block bg-lime text-dodgers font-condensed uppercase tracking-widest text-[13px] px-3 py-1.5 mt-6">
              Sponsorship · Activation · Programmi pluriennali
            </span>
          </RevealOnScroll>
          <RevealOnScroll delay={0.15}>
            <h1
              className="display-h1 mt-8 max-w-full"
              style={{
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                fontSize: "clamp(36px, 7vw, 88px)",
              }}
            >
              <span className="block">Sponsorship</span>
              <span className="block">che generano</span>
              <span className="block text-lime">VALORE.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.25}>
            <p className="mt-10 body-lg opacity-85 max-w-[68ch]">
              Trasformiamo le sponsorship da costo di visibilità a
              piattaforma di business, contenuto e impatto sociale.
              Programmi pluriennali con KPI dichiarati, asset attivati al
              massimo, framework di misurazione condivisi tra brand e
              property.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.32}>
            <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] opacity-85">
              <span>Per — Brand sponsor, club, federazioni, agenzie commerciali</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Durata — 6-24 mesi</span>
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
              <span>Output — Platform sponsorship attivata, framework, reporting</span>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL PROBLEMA */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-problema">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Il problema</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  La maggior parte delle sponsorship italiane spreca i
                  propri asset.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Quando un brand firma una sponsorship con un club, ottiene
                  un contratto pieno di asset: visibilità on-site,
                  contenuti, eventi, ospitalità, accessi privilegiati,
                  diritti d'uso del logo, attivazioni di matchday. Sulla
                  carta è un investimento ricco. Nella pratica, la maggior
                  parte di quegli asset non viene mai attivata davvero.
                  Restano nel contratto come voci formali, ma producono
                  valore zero.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  La ragione è strutturale. La sponsorship viene venduta
                  dalla property come una checklist di "diritti" — più
                  diritti, più costa. Ma una volta firmato il contratto, la
                  responsabilità di trasformare quei diritti in valore
                  ricade interamente sul brand, che spesso non ha il team,
                  il tempo o la cultura per farlo. Il risultato è un
                  rapporto in cui entrambe le parti hanno investito (la
                  property in opportunità commerciali, il brand in budget)
                  e in cui il valore generato è una frazione di quello
                  potenziale.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Una sponsorship moderna non si vende a diritti. Si vende a
                  piattaforma. Significa: cosa costruiremo insieme nei
                  12-24 mesi di durata del contratto, che KPI ci diamo,
                  come ci misuriamo, come dividiamo il valore generato.
                  Questo cambia tutto — il modo di scrivere il contratto,
                  il modo di lavorare durante l'anno, il modo di rinnovare.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — COSA FACCIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-cosa-facciamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12">
            <div className="col-span-12 md:col-span-5">
              <RevealOnScroll><SectionEyebrow>Cosa facciamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Progettiamo e attiviamo platform sponsorship.
                </h2>
              </RevealOnScroll>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-7 text-[1.0625rem] md:text-[1.125rem] leading-[1.6] max-w-[760px] opacity-90">
              <RevealOnScroll delay={0.15}>
                <p>
                  Lavoriamo su entrambi i lati del tavolo. Per i brand
                  sponsor, progettiamo l'attivazione di partnership
                  esistenti o nuove come piattaforme integrate: definiamo
                  cosa costruire insieme alla property, su quali KPI
                  misurarsi, come usare ogni asset contrattuale per generare
                  valore reale. Per le property sportive (club,
                  federazioni, leghe), riprogettiamo la struttura
                  commerciale delle sponsorship in modo che attraggano
                  brand interessati a piattaforme, non a checklist.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.2}>
                <p>
                  Il nostro lavoro è metodologico ma anche operativo. Non
                  ci limitiamo a scrivere un piano — quando il cliente lo
                  richiede, eseguiamo l'attivazione end-to-end: dalla
                  negoziazione del contratto al piano di matchday, dalla
                  produzione dei contenuti coordinata con il club al
                  reporting di fine anno. Quando il cliente preferisce
                  eseguire internamente, lasciamo un framework documentato
                  che la sua organizzazione può applicare.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.25}>
                <p>
                  Lo Sponsorship Impact Optimizer è il nostro framework
                  proprietario per ottimizzare il portafoglio sponsorship
                  di un brand. È progettato per aziende che hanno almeno
                  3-4 sponsorship attive in parallelo e vogliono capire
                  quali stanno funzionando, quali sono dormienti, e dove
                  c'è valore non realizzato.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL METODO */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-metodo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Il metodo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Quattro passi. Per ogni sponsorship.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {METHOD_STEPS.map((step, i) => (
              <RevealOnScroll key={step.num} delay={i * 0.05}>
                <div
                  className="border border-lime p-6 md:p-8 bg-dodgers/[0.02] h-full flex flex-col"
                  data-testid={`method-step-${step.num}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-lime text-3xl md:text-4xl leading-none">
                      {step.num}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl leading-tight uppercase">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {step.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 5 — COSA INCLUDIAMO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-includiamo">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Cosa includiamo</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quello che ricevi.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="deliverables-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Output</th>
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Quando</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Per chi</th>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map((d, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top">{d.output}</td>
                      <td className="py-4 pr-6 align-top opacity-80">{d.when}</td>
                      <td className="py-4 align-top opacity-80">{d.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 6 — PER CHI È */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-per-chi-e">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Per chi è</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Quattro profili.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {PROFILES.map((p, i) => (
              <RevealOnScroll key={i} delay={i * 0.06}>
                <div
                  className="border border-lime p-6 md:p-8 h-full"
                  data-testid={`profile-${i}`}
                >
                  <h3 className="font-display text-lg md:text-xl leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[1rem] leading-[1.6] opacity-90 max-w-[52ch]">
                    {p.body}
                  </p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 7 — CASI DI RIFERIMENTO */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-casi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Casi di riferimento</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Platform sponsorship, in azione.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {REFERENCE_CASES.map((c, i) => (
              <RevealOnScroll key={c.to} delay={i * 0.06}>
                <Link
                  to={c.to}
                  className="group block focus:outline-none"
                  data-hover="true"
                  data-testid={`case-card-${i}`}
                >
                  <div className="overflow-hidden bg-galactico/[0.05]" style={{ aspectRatio: "4 / 3" }}>
                    <img
                      src={c.image}
                      alt={c.title}
                      loading="lazy"
                      className="block w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                      draggable="false"
                    />
                  </div>
                  <span className="block mt-4 font-condensed uppercase tracking-widest text-lime text-[11px]">
                    ● {c.chip}
                  </span>
                  <h3 className="mt-2 font-display text-xl md:text-2xl leading-tight group-hover:text-lime transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.55] opacity-85">{c.kpi}</p>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 8 — MISURAZIONE & KPI */}
      <section className="section-pad bg-galactico text-dodgers" data-testid="section-kpi">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Misurazione</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">
                  Numeri condivisi tra brand e property.
                </h2>
              </RevealOnScroll>
            </div>
          </div>
          <RevealOnScroll delay={0.05}>
            <div className="overflow-x-auto" data-testid="kpi-table">
              <table className="w-full text-left text-[14px] md:text-[15px]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
                <thead>
                  <tr className="border-b border-mandrogni/40">
                    <th className="py-4 pr-6 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal w-[28%]">Tipo di KPI</th>
                    <th className="py-4 font-condensed uppercase tracking-widest text-lime text-[12px] md:text-[13px] font-normal">Esempi</th>
                  </tr>
                </thead>
                <tbody>
                  {KPI_TYPES.map((k, i) => (
                    <tr key={i} className="border-b border-mandrogni/20">
                      <td className="py-4 pr-6 align-top font-display text-base md:text-lg">{k.type}</td>
                      <td className="py-4 align-top opacity-90">{k.examples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — FAQ */}
      <section className="section-pad bg-dodgers text-galactico" data-testid="section-faq">
        <div className="container-os">
          <div className="grid grid-cols-12 gap-8 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-8">
              <RevealOnScroll><SectionEyebrow>Domande frequenti</SectionEyebrow></RevealOnScroll>
              <RevealOnScroll delay={0.1}>
                <h2 className="display-h2 mt-6">Le domande più frequenti.</h2>
              </RevealOnScroll>
            </div>
          </div>
          <div className="max-w-[960px]" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <RevealOnScroll key={i} delay={i * 0.04}>
                <details
                  className="group border-b border-galactico/20 py-5 md:py-6"
                  data-testid={`faq-${i}`}
                >
                  <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
                    <span className="font-display text-lg md:text-xl leading-tight pr-4">
                      {f.q}
                    </span>
                    <span
                      className="flex-shrink-0 mt-1 text-lime transition-transform duration-300 group-open:rotate-45"
                      aria-hidden="true"
                    >
                      <Plus size={22} strokeWidth={2.2} />
                    </span>
                  </summary>
                  <p className="mt-4 text-[15px] md:text-[1rem] leading-[1.7] opacity-90 max-w-[760px]">
                    {f.a}
                  </p>
                </details>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 10 — CONTATTO IN-PAGE */}
      <section
        className="bg-galactico text-dodgers py-24 md:py-32 border-t border-dodgers/10"
        data-testid="section-cta-finale"
      >
        <div className="container-os max-w-3xl">
          <RevealOnScroll>
            <SectionEyebrow>● Cominciamo</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.05}>
            <h2 className="display-h2 mt-6 max-w-[28ch]" data-testid="cta-h2">
              Trasforma la tua sponsorship in una vera <span className="text-lime">PIATTAFORMA</span>.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <p className="mt-8 body-lg opacity-85 max-w-[62ch]">
              Compila il form qui sotto, ti rispondiamo entro 24 ore lavorative.
              Una call di 30 minuti per capire se siamo gli interlocutori giusti.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <div className="mt-10">
              <ContactForm
                tone="dark"
                defaultSubject="Richiesta consulenza — Sponsorship & Impact Programs"
                compact
              />
            </div>
          </RevealOnScroll>
        </div>
      </section>
    </>
  );
};

export default ServizioSponsorship;
